/**
 * A aba do CRM avisa ao service worker que está À VISTA (#536).
 *
 * ── O defeito que isto resolve ───────────────────────────────────────────
 *
 * O servidor manda o push para o navegador sempre — ele não sabe se há aba
 * aberta. Quem decide mostrar ou não é o service worker, e a regra é: com uma
 * aba do CRM à vista, o card basta, e o aviso do sistema não aparece.
 *
 * Para saber se há aba à vista, o service worker pergunta ao navegador
 * (`clients.matchAll`). O Safari responde mal: a aba aberta antes de o
 * service worker assumir fica de fora da lista. O service worker concluía
 * "nenhuma aba à vista" e mostrava o aviso POR CIMA do card — inclusive da
 * conversa que a pessoa estava lendo (medido em 28/09/2026).
 *
 * ── O carimbo ────────────────────────────────────────────────────────────
 *
 * Enquanto está à vista, a aba grava a hora num cache que o service worker
 * também lê, e renova a cada `INTERVALO_MS`. Escondida ou fechada, apaga. O
 * service worker considera "à vista" um carimbo mais novo que
 * `VALIDADE_MS` — o que sobrevive a ele ser desligado e religado pelo
 * navegador, coisa que um aviso guardado na memória dele não sobreviveria.
 *
 * O nome do cache e da entrada, e os dois tempos, estão repetidos em
 * `public/sw-notificacoes.js`, que não importa módulo: mudou aqui, muda lá.
 */

import { useEffect } from 'react';

export const CACHE_DO_ESTADO = 'cliqcrm-notificacoes';
export const ENTRADA_DA_ABA_VISIVEL = '/cliqcrm/aba-visivel';
export const INTERVALO_MS = 5_000;
export const VALIDADE_MS = 15_000;

/** O carimbo ainda vale? Puro, para o teste — o service worker faz a mesma conta. */
export function carimboVale(carimbo: number, agora: number): boolean {
  return Number.isFinite(carimbo) && carimbo > 0 && agora - carimbo < VALIDADE_MS;
}

function cacheDisponivel(): boolean {
  return typeof caches !== 'undefined';
}

/**
 * Alguma aba do CRM está à vista? Lido pela aba ESCONDIDA, que já apagou o
 * próprio carimbo: um carimbo válido aqui é de outra aba. Sem cache, `false` —
 * a aba escondida volta a disputar o som como se fosse a única.
 */
export async function algumaAbaAVista(agora: number = Date.now()): Promise<boolean> {
  if (!cacheDisponivel()) return false;
  try {
    const cache = await caches.open(CACHE_DO_ESTADO);
    const resposta = await cache.match(ENTRADA_DA_ABA_VISIVEL);
    return resposta ? carimboVale(Number(await resposta.text()), agora) : false;
  } catch {
    return false;
  }
}

async function gravar(agora: number): Promise<void> {
  const cache = await caches.open(CACHE_DO_ESTADO);
  await cache.put(ENTRADA_DA_ABA_VISIVEL, new Response(String(agora)));
}

async function apagar(): Promise<void> {
  const cache = await caches.open(CACHE_DO_ESTADO);
  await cache.delete(ENTRADA_DA_ABA_VISIVEL);
}

/**
 * Montado na casca: vale em qualquer tela. Falha de cache é silenciosa — sem
 * ela o service worker volta a depender só do `matchAll`, que é o que havia.
 */
export function useAvisarAbaVisivel(): void {
  useEffect(() => {
    if (!cacheDisponivel()) return;

    const renovar = (): void => {
      if (document.visibilityState === 'visible') void gravar(Date.now()).catch(() => undefined);
    };
    const aoMudarVisibilidade = (): void => {
      if (document.visibilityState === 'visible') renovar();
      else void apagar().catch(() => undefined);
    };
    const aoSair = (): void => void apagar().catch(() => undefined);

    renovar();
    const relogio = window.setInterval(renovar, INTERVALO_MS);
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    window.addEventListener('pagehide', aoSair);

    return () => {
      window.clearInterval(relogio);
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
      window.removeEventListener('pagehide', aoSair);
      void apagar().catch(() => undefined);
    };
  }, []);
}
